<template>
  <Teleport to="body">
    <Transition name="cal-popover">
      <div
        v-if="event"
        ref="floating"
        v-click-away="onClickAway"
        class="cal-popover"
        :style="floatingStyles"
        v-bind="passThrough('popover')"
        @pointerenter="onPointerEnter"
        @pointerleave="onPointerLeave"
      >
        <div class="cal-popover__surface">
          <!-- Hosts can replace the whole card while keeping the positioning logic -->
          <slot
            name="content"
            :event="event"
          >
            <div class="cal-popover__card">
              <div
                class="cal-popover__accent"
                :style="{ backgroundColor: accentColor }"
              />

              <div class="cal-popover__body">
                <div class="cal-popover__heading">
                  <Icon
                    v-if="event.icon"
                    :type="event.icon"
                    class="cal-popover__icon"
                    :style="{ color: accentColor }"
                  />
                  <span class="cal-popover__title">{{ event.title }}</span>
                </div>

                <div class="cal-popover__meta">
                  <Icon
                    type="clock"
                    class="cal-popover__meta-icon"
                  />
                  <span>{{ dateTimeLabel }}</span>
                </div>

                <div
                  v-if="event.timezone"
                  class="cal-popover__meta"
                >
                  <Icon
                    type="globe"
                    class="cal-popover__meta-icon"
                  />
                  <span>{{ event.timezone }}</span>
                </div>

                <div
                  v-if="event.description"
                  class="cal-popover__description"
                >
                  <div class="cal-popover__description-label">{{ $t('ui.common.description') }}</div>
                  <p class="cal-popover__description-text">{{ event.description }}</p>
                </div>
              </div>
            </div>
          </slot>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import {
  autoUpdate,
  flip,
  limitShift,
  offset,
  shift,
  useFloating,
  type Middleware,
  type VirtualElement
} from '@floating-ui/vue'
import { computed, ref, toRef } from 'vue'

// components
import Icon from '@/components/icon/Icon.vue'

// types
import type { CalendarEvent, CalendarEventPopoverTrigger } from '@/types/components/calendar'

// utilities
import { resolveCalendarColor } from '@/utilities/calendar/color'
import { formatEventDateTime } from '@/utilities/calendar/format'
import { useCalendarPassThrough } from '@/utilities/calendar/passthrough'
import { shiftPopoverAwayFromObstacles } from '@/utilities/calendar/popover'

const props = withDefaults(
  defineProps<{
    /** Event to describe, or null when the popover is closed */
    event: CalendarEvent | null
    /** Element the popover is anchored to */
    anchor: HTMLElement | VirtualElement | null
    /** Panels the popover must not cover, such as the overflow list */
    avoid?: HTMLElement | null
    /** 'side' sits left/right of month chips; 'block' sits above/below time-grid events */
    placementMode?: 'side' | 'block'
    /** Whether the card stays until click-away, or follows hover */
    trigger?: CalendarEventPopoverTrigger
    /** Use 12-hour clock labels */
    hour12?: boolean
    /** Calendar-level fallback timezone */
    timezone?: string
  }>(),
  {
    hour12: false,
    timezone: undefined,
    avoid: null,
    placementMode: 'side',
    trigger: 'click'
  }
)

const emit = defineEmits<{
  /** Pointer moved onto the popover, so the close timer should be cancelled */
  'keep-open': []
  /** Pointer left the popover */
  'request-close': []
  /** Click-away in click mode, hide immediately */
  dismiss: []
}>()

const passThrough = useCalendarPassThrough()

/** The popover element, positioned by floating-ui */
const floating = ref<HTMLElement | null>(null)

/** Space the card needs to the right of its anchor before we give up and flip */
const POPOVER_RIGHT_SPACE = 340

/**
 * Prefers below a time-grid event, or beside a month/overflow chip.
 */
const placement = computed(() => {
  // Week and day events sit in a tall column; below/above stays attached to the chip
  if (props.placementMode === 'block') {
    return 'bottom'
  }

  const anchor = props.anchor

  // Closed, or a virtual element we cannot measure yet
  if (!anchor || typeof anchor.getBoundingClientRect !== 'function') {
    return 'right-start'
  }

  const rect = anchor.getBoundingClientRect()

  return window.innerWidth - rect.right < POPOVER_RIGHT_SPACE ? 'left-start' : 'right-start'
})

/**
 * After flip/shift, push the card so it does not cover the overflow list.
 */
const avoidObstacles: Middleware = {
  name: 'avoidObstacles',
  fn({ x, y, rects }) {
    // Time-grid cards sit above/below the event; only the overflow list is an obstacle
    if (props.placementMode === 'block' || !(props.avoid instanceof HTMLElement)) {
      return {}
    }

    const rect = props.avoid.getBoundingClientRect()
    const next = shiftPopoverAwayFromObstacles(
      { x, y, width: rects.floating.width, height: rects.floating.height },
      [{ x: rect.left, y: rect.top, width: rect.width, height: rect.height }],
      { gap: 12, viewportPadding: 8, viewportWidth: window.innerWidth }
    )

    // Unchanged after the pass
    if (next.x === x && next.y === y) {
      return {}
    }

    return next
  }
}

const middleware = computed(() => [
  offset(8),
  flip({
    padding: 8,
    fallbackPlacements:
      props.placementMode === 'block'
        ? ['top', 'top-start', 'bottom-start']
        : ['left-start', 'left-end', 'right-start', 'right-end']
  }),
  shift({ padding: 8, limiter: limitShift() }),
  avoidObstacles
])

const { floatingStyles } = useFloating(toRef(props, 'anchor'), floating, {
  placement,
  middleware,
  strategy: 'fixed',
  whileElementsMounted: autoUpdate
})

/** Accent colour taken from the event */
const accentColor = computed(() => resolveCalendarColor(props.event?.color))

/** Full date and time line */
const dateTimeLabel = computed(() =>
  props.event ? formatEventDateTime(props.event, { hour12: props.hour12, timezone: props.timezone }) : ''
)

/**
 * Hover-mode only: keep the card open while the pointer is on it.
 */
function onPointerEnter(): void {
  try {
    // Click-mode cards stay until dismiss; hover enter would fight that
    if (props.trigger !== 'hover') {
      return
    }

    emit('keep-open')
  } catch {
    emit('keep-open')
  }
}

/**
 * Hover-mode only: start the close delay when the pointer leaves the card.
 */
function onPointerLeave(): void {
  try {
    if (props.trigger !== 'hover') {
      return
    }

    emit('request-close')
  } catch {
    emit('request-close')
  }
}

/**
 * Closes a click-triggered card when the pointer lands outside it.
 * Clicks on event chips and agenda rows are ignored so those controls can toggle.
 * @param event - Document click from vue3-click-away
 */
function onClickAway(event: Event): void {
  try {
    if (props.trigger !== 'click') {
      return
    }

    const path = typeof event.composedPath === 'function' ? event.composedPath() : []
    const isOnToggleTarget = path.some(
      node =>
        node instanceof HTMLElement &&
        Boolean(node.closest('.cal-event, .cal-agenda__item'))
    )

    // The chip or agenda row owns open/close; treating it as outside would fight the toggle
    if (isOnToggleTarget) {
      return
    }

    emit('dismiss')
  } catch {
    emit('dismiss')
  }
}
</script>

<style scoped>
.cal-popover {
  z-index: 10002;
  width: min(20rem, calc(100vw - 1rem));
  min-width: 0;
}

.cal-popover__surface {
  width: 100%;
  min-width: 0;
  overflow: hidden;
  background-color: var(--bg-primary);
  border: 1px solid var(--border-secondary);
  border-radius: var(--rounded-lg);
  box-shadow: var(--shadow-lg);
  transform-origin: center top;
}

.cal-popover-enter-active {
  transition: opacity 0.18s ease;
}

.cal-popover-leave-active {
  transition: opacity 0.14s ease;
}

.cal-popover-enter-active .cal-popover__surface,
.cal-popover-leave-active .cal-popover__surface {
  transition: transform 0.18s cubic-bezier(0.16, 1, 0.3, 1);
}

.cal-popover-leave-active .cal-popover__surface {
  transition-duration: 0.14s;
}

.cal-popover-enter-from,
.cal-popover-leave-to {
  opacity: 0;
}

.cal-popover-enter-from .cal-popover__surface,
.cal-popover-leave-to .cal-popover__surface {
  transform: translateY(6px) scale(0.98);
}

.cal-popover__card {
  display: flex;
  min-width: 0;
  overflow: hidden;
}

.cal-popover__accent {
  flex-shrink: 0;
  width: 0.25rem;
  align-self: stretch;
}

.cal-popover__body {
  display: flex;
  flex-direction: column;
  gap: var(--s-2);
  padding: var(--s-3) var(--s-3-5);
  min-width: 0;
}

.cal-popover__heading {
  display: flex;
  align-items: center;
  gap: var(--s-2);
  min-width: 0;
}

.cal-popover__icon {
  flex-shrink: 0;
}

.cal-popover__title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-primary);
}

.cal-popover__meta {
  display: flex;
  align-items: center;
  gap: var(--s-2);
  font-size: var(--text-xs);
  color: var(--text-secondary);
}

.cal-popover__meta-icon {
  flex-shrink: 0;
  color: var(--text-quaternary);
}

.cal-popover__description {
  padding-top: var(--s-2);
  border-top: 1px solid var(--border-secondary);
}

.cal-popover__description-label {
  margin-bottom: 0.125rem;
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--text-tertiary);
}

.cal-popover__description-text {
  margin: 0;
  font-size: var(--text-xs);
  line-height: 1.5;
  color: var(--text-secondary);
  /* Keep the hover card scannable rather than letting long notes take over */
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
